/**
 * useRowBodies — 목록 행의 조판 본문을 **눈에 든 것만** 받아 두는 훅.
 *
 * 오너 요구: "너무 많은 문제를 포함하고 있어서 한번에 표출하는데에 부하가 걸리면 최소한의
 * 부분 요청 기능을 구현". 그 「부분」이 **데이터**에만 걸린다는 것이 이 훅의 전제다 —
 * 행 자체는 전부 그린다. 잘라 그리면 순번·[위로]·[빼기]가 창 상대 인덱스로 어긋나
 * 3번을 올렸는데 13번이 올라간다.
 *
 * ── 무엇을 아끼나 ─────────────────────────────────────────────────────────
 * 200문항짜리를 열면 조판 본문 200벌이 한꺼번에 온다(그리고 KaTeX 가 200벌 조판된다).
 * 여기서는 화면에 든 20개만 받는다. 안 내리면 요청은 한 번뿐이고, 내린 만큼만 늘어난다.
 *
 * ── 접두 창이 아니라 관찰이다 ─────────────────────────────────────────────
 * "앞에서부터 N개"로 하면 500번 행 하나를 보려고 1~500 을 전부 받는다. 그래서 IntersectionObserver
 * 하나로 행 전부를 지켜보고, **화면에 든 것**만 큐에 넣는다. 저장소 선례가 같은 모양이다
 * (ProblemPreviewCards.js:341 — 단일 관찰자 + 배치 로드).
 *
 * ── 두 번 묻지 않는 법 ────────────────────────────────────────────────────
 * 자국(seen)은 **ref** 에 남기고 **요청을 보내기 전에** 남긴다.
 * 본문이 없는 문항은 지도에 **키가 안 들어오는 것이 계약**이라(problemBodyV2, 계약 §9),
 * "받은 것으로 표시하기"로는 자국이 남지 않아 렌더마다 같은 요청이 다시 나간다.
 * 이것이 같은 파일의 개념 보충이 쓰는 「물어본 id 를 빈 배열로 전부 채운다」와 **정반대 규칙**이다 —
 * 두 트랙의 방지 수단이 서로 다른 이유가 여기 있다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchProblemItemsV2 } from './problemBodyV2';
import { toBodyHtmlMap } from './lexicalHtml';

/** 한 번에 묻는 문항 수 — 조립 창의 검색 한 페이지와 같은 값(새 숫자를 발명하지 않는다) */
const BODY_BATCH = 20;
/**
 * 관찰 이벤트를 모아 두는 시간(ms). 스크롤 한 번에 열 줄이 스치면 열 번 요청하지 않고 한 번 묻는다.
 * 120ms 는 사람이 「눌렀는데 반응이 없다」고 느끼는 문턱(약 200ms)보다 아래다.
 */
const FLUSH_MS = 120;
/** 화면에 들기 **전에** 미리 받아 둘 거리 — 스크롤이 멈추기 전에 본문이 차 있게 한다 */
const ROOT_MARGIN = '400px 0px';

/**
 * 스크롤을 맡은 조상 — 관찰의 기준(root)이자, 통계 열의 높이를 재는 자다.
 *
 * 클래스 이름(`.overflow-y-auto`)으로 찾지 않는다. 그것은 담는 쪽의 사정이고, 그쪽이 클래스를
 * 바꾸는 순간 이 화면이 조용히 옛 기준으로 굳는다. 계산된 값으로 찾으면 누가 감싸든 따라온다.
 *
 * 목록이 **자기 안에서 스크롤**하게 된 뒤로 이 함수가 관찰에도 필요해졌다(오너 지시 2026-08-28).
 * root 를 안 주면 기준이 뷰포트라, rootMargin 400px 이 목록 칸이 아니라 화면 기준으로 재진다 —
 * 목록 아래쪽이 화면 안에 있기만 하면 안 보이는 행까지 미리 받아 온다.
 */
export function findScrollParent(el) {
  let p = el?.parentElement;
  while (p != null && p !== document.body) {
    const oy = window.getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll') return p;
    p = p.parentElement;
  }
  return null;
}

/**
 * @param getAccessToken 인증 토큰 공급자
 * @param rowsKey        문항 id 를 이은 문자열. 이 값이 바뀌면 관찰을 다시 건다 —
 *                       문항이 늘거나 순서가 바뀌면 지켜볼 `<li>` 도 달라지기 때문이다.
 */
export default function useRowBodies(getAccessToken, rowsKey = '') {
  // pid → HTML. 본문이 없거나 변환에 실패한 문항은 **키가 없다**(그 행이 평문 요약으로 폴백한다)
  const [htmls, setHtmls] = useState({});
  // pid → v2 검색 item. 본문과 같은 응답에 난이도·개념·라벨이 실려 오므로
  // 미리보기가 배지를 그리려고 같은 문항을 한 번 더 묻지 않게 통째로 보존한다.
  const [items, setItems] = useState({});
  // 응답을 받은 pid. item 자체가 없거나 body_lexical이 null인 경우에도
  // 로딩 중으로 오인하지 않기 위해 지도와 따로 기억한다.
  const [completedRows, setCompletedRows] = useState(() => new Set());
  // 한 번이라도 화면에 든 pid — 이 집합에 든 행만 본문 부품을 마운트한다
  const [seenRows, setSeenRows] = useState(() => new Set());
  // 묶음이 통째로 실패한 pid. 「응답은 왔는데 그 id 만 없는 경우」와 구별된다 — 그쪽은 문항이
  // 실제로 없는 것(삭제됐거나 status≠999)이라 다시 물어도 같은 답이다.
  const [failedIds, setFailedIds] = useState([]);

  const askedRef = useRef(new Set());   // 요청을 보낸 자국(성공·실패 무관)
  const queueRef = useRef(new Set());   // 아직 안 보낸 pid
  const timerRef = useRef(null);
  const busyRef = useRef(false);        // 한 번에 한 묶음만 — 스크롤이 빨라도 요청이 겹치지 않는다
  const listRef = useRef(null);         // 관찰 대상들이 사는 컨테이너

  // StrictMode 는 마운트 → cleanup → 마운트를 돌린다. 마운트마다 되살리지 않으면 dev 에서
  // false 로 굳어 응답이 와도 상태를 못 갈고 **영원히 빈 칸**이다(ProblemPreviewV2 와 같은 함정).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  /** 큐에서 한 묶음(최대 BODY_BATCH)을 꺼내 받아 온다. 남으면 스스로 다음 묶음을 부른다 */
  const drain = useCallback(async () => {
    if (busyRef.current) return;
    const take = Array.from(queueRef.current).slice(0, BODY_BATCH);
    if (take.length === 0) return;
    take.forEach((id) => queueRef.current.delete(id));
    busyRef.current = true;
    try {
      // 자국은 **여기서** 남긴다 — await 뒤로 미루면 그 사이의 렌더가 같은 id 를 다시 큐에 넣는다
      take.forEach((id) => askedRef.current.add(id));
      const loaded = await fetchProblemItemsV2(take, getAccessToken, { withBody: true });
      if (!aliveRef.current) return;
      if (loaded == null) {
        // 묶음이 통째로 실패했다 — 되돌릴 길을 남긴다. 조립 창은 「창이 닫힐 때까지 안 묻는다」를
        // 대가로 받았지만, 여기는 행이 수백 개일 수 있어 그 대가가 너무 크다.
        setFailedIds((prev) => Array.from(new Set([...prev, ...take])));
        return;
      }
      setItems((prev) => ({ ...prev, ...loaded }));
      const raw = {};
      Object.entries(loaded).forEach(([key, item]) => {
        if (item?.body_lexical != null) raw[key] = item.body_lexical;
      });
      let html = {};
      try {
        html = await toBodyHtmlMap(raw);
      } catch (e) {
        // 변환기(lexical·katex)를 못 받았다 — 여기서 던지면 화면이 통째로 넘어진다
        console.error('행 본문 변환기 로드 실패:', e);
        if (aliveRef.current) setFailedIds((prev) => Array.from(new Set([...prev, ...take])));
        return;
      }
      if (!aliveRef.current) return;
      // 완료는 본문 변환까지 끝난 뒤에만 세운다. 동적 변환기 청크가 느릴 때 누락 문구가
      // 먼저 번쩍이거나, 변환 실패와 완료가 동시에 참이 되는 상태를 만들지 않는다.
      setCompletedRows((prev) => {
        const next = new Set(prev);
        take.forEach((id) => next.add(String(id)));
        return next;
      });
      if (Object.keys(html).length === 0) return;
      // id 를 키로 합쳐 넣는다 — 늦게 온 응답이 먼저 온 것을 덮을 수 없다
      setHtmls((prev) => ({ ...prev, ...html }));
    } finally {
      busyRef.current = false;
      // 큐에 남은 것이 있으면 이어서. 재귀가 아니라 다음 틱으로 넘겨 스택을 쌓지 않는다.
      if (aliveRef.current && queueRef.current.size > 0) {
        timerRef.current = setTimeout(() => { drain(); }, 0);
      }
    }
  }, [getAccessToken]);

  /** 관찰에 든 pid 를 모았다가 한 번에 처리 — 스크롤 한 번에 요청 열 번을 막는다 */
  const enqueue = useCallback((ids) => {
    let added = false;
    ids.forEach((id) => {
      if (askedRef.current.has(id) || queueRef.current.has(id)) return;
      queueRef.current.add(id);
      added = true;
    });
    // 본문을 아직 안 받았어도 **행은 지금 마운트한다** — 그래야 자리(h-16)가 잡히고
    // 본문이 도착했을 때 목록이 들썩이지 않는다
    setSeenRows((prev) => {
      const next = new Set(prev);
      let grew = false;
      ids.forEach((id) => { if (!next.has(id)) { next.add(id); grew = true; } });
      return grew ? next : prev;
    });
    if (!added) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { drain(); }, FLUSH_MS);
  }, [drain]);

  /**
   * 관찰 배선 — 컨테이너 안의 `[data-body-pid]` 를 전부 지켜본다.
   * `rowsKey` 가 바뀌면 다시 건다. 이미 자국이 있는 pid 는 콜백에서 걸러지므로,
   * 다시 걸리며 즉시 발화하는 것들이 요청을 다시 내지는 않는다.
   */
  useEffect(() => {
    const root = listRef.current;
    if (root == null) return undefined;
    const elements = Array.from(root.querySelectorAll('[data-body-pid]'));
    if (typeof IntersectionObserver === 'undefined') {
      // 구형 WebView에서도 뼈대가 영구 로딩으로 굳지 않게 전부 큐에 넣는다. 요청은 drain의
      // 20개 제한과 직렬 실행을 그대로 거치므로 한 번에 거대한 payload가 나가지는 않는다.
      enqueue(elements.map((el) => el.getAttribute('data-body-pid')).filter(Boolean));
      return undefined;
    }
    // 목록이 자기 안에서 스크롤하므로 그 칸을 기준으로 본다(위 findScrollParent 주석)
    const io = new IntersectionObserver((entries) => {
      const hit = [];
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const pid = e.target.getAttribute('data-body-pid');
        if (pid) hit.push(pid);
      });
      if (hit.length > 0) enqueue(hit);
    }, { root: findScrollParent(root), rootMargin: ROOT_MARGIN, threshold: 0 });
    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rowsKey, enqueue]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  /** 실패한 묶음만 다시 — 자국을 지워 다음 관찰에서 새로 묻게 한다 */
  const retryFailed = useCallback(() => {
    const ids = failedIds;
    if (ids.length === 0) return;
    ids.forEach((id) => askedRef.current.delete(id));
    setFailedIds([]);
    enqueue(ids);
  }, [failedIds, enqueue]);

  return {
    listRef,
    htmlOf: (pid) => htmls[String(pid)] ?? null,
    itemOf: (pid) => items[String(pid)] ?? null,
    isSeen: (pid) => seenRows.has(String(pid)),
    isComplete: (pid) => completedRows.has(String(pid)),
    isFailed: (pid) => failedIds.includes(String(pid)),
    failedCount: failedIds.length,
    retryFailed,
  };
}
